<template>
    <notification-modal v-model:is-open="showSubmittedTicketModal">
        <template #title-content>
            Support Ticket Submitted
        </template>
        <p>
            Your support ticket has been created and our support team will be in
            contact with you with more information as soon as possible.
        </p>
    </notification-modal>

    <div v-if="troubleshootingGroups.length" class="py-8 w-full flex justify-center mb-6">
        <div class="w-2/5 flex flex-row gap-6">
            <div class="w-2/5 self-start text-center">
                <h3 class="text-lg text-left font-semibold pt-0 pb-1">Before You Contact Us</h3>
                <p class="text-gray-600 text-left">
                    To help us provide quicker support, please take a moment to try the common troubleshooting steps
                    below. Check off any you've already completed.  If you're still experiencing issues after
                    completing these steps, we'll be happy to assist further. Please submit a support ticket below.
                </p>
            </div>
            <div class="w-3/5">
                <div
                    v-for="group in troubleshootingGroups"
                    :key="group.category"
                    class="mb-4"
                >
                    <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-300 pb-1 mb-2">
                        {{ group.category }}
                    </h4>
                    <div class="flex flex-col gap-2">
                        <label
                            v-for="(step, index) in group.steps"
                            :key="index"
                            class="flex items-start gap-2 cursor-pointer"
                        >
                            <input
                                v-model="checkedSteps"
                                :value="`${group.category}: ${step.label}`"
                                class="mt-1"
                                type="checkbox"
                            />
                            <div>
                                <span class="font-medium">
                                    <a v-if="step.link" :href="step.link" target="_blank">
                                        {{ step.label }}
                                    </a>
                                    <span v-else>{{ step.label }}</span>
                                </span>
                                <p v-if="step.description" class="text-sm text-gray-500">{{ step.description }}</p>
                            </div>
                        </label>
                    </div>
                </div>

                <!-- Other troubleshooting steps -->
                <div class="mt-4">
                    <label class="block font-medium mb-1">Other troubleshooting steps taken</label>
                    <textarea
                        v-model="otherTroubleshootingSteps"
                        class="w-full rounded-md border-2 border-gray-400 p-2 text-sm"
                        placeholder="Describe any other steps you've taken..."
                        rows="3"
                    ></textarea>
                </div>
            </div>
        </div>
    </div>

    <div class="w-full flex justify-center">
        <div class="w-2/5 flex flex-col justify-center">
            <div class="">
                <!-- Reply Method -->
                <div class="flex flex-row items-center justify-end mb-2">
                    <label class="w-2/5" for="reply_method">How would you like to receive replies</label>
                    <div class="w-3/5">
                        <select2
                            id="reply_method"
                            v-model:value="replyMethod"
                            :close-on-select="true"
                            :options="supportMethods">
                        </select2>
                    </div>
                </div>

                <!-- Support Contact Method -->
                <div class="w-full flex flex-row justify-end">
                    <div class="w-3/5 my-2">
                        <!-- Phone Number -->
                        <div v-show="replyMethod === 'phone'">
                            <floating-input
                                v-model="phoneNumber"
                                :required="replyMethod === 'phone'"
                                :value="phoneNumber"
                                label="Phone Number"
                            />
                        </div>

                        <!-- Email Address -->
                        <div v-show="replyMethod === 'email'">
                            <floating-input
                                v-model:value="emailAddress"
                                :required="replyMethod === 'email'"
                                label="Email Address"
                            />
                        </div>
                    </div>
                </div>

                <!-- List of Points -->
                <div class="w-full flex flex-row justify-end items-center">
                    <div class="pr-4 font-semibold">
                        Select the points that you are having issues with
                    </div>
                    <div class="w-3/5 mb-4">
                        <select2 v-model:options="devices"
                                 v-model:value="selectedDevices"
                                 :multiple="true"
                                 :allow-clear="true"
                                 :can-select-all="true"
                                 name="points"
                        ></select2>
                    </div>
                </div>

                <!-- Customer Description -->
                <div class="">
                    <textarea
                        id="description"
                        v-model="description"
                        class="ticket-field rounded-md border-2 border border-gray-400 p-1"
                        placeholder="Enter a Description of your problem"
                        required
                        @input="autoResizeTextarea"
                    ></textarea>
                </div>

                <!-- KB Article Suggestions -->
                <div v-if="kbLoading" class="mt-3 flex items-center gap-2 text-sm text-gray-500">
                    <span :class="iconLoading"></span>
                    <span>Finding relevant help articles...</span>
                </div>

                <div v-if="!kbLoading && kbSuggestions.length > 0" class="mt-3">
                    <p class="mb-2 text-sm font-medium text-gray-600">These articles may help with your issue:</p>
                    <div class="space-y-2">
                        <div
                            v-for="article in kbSuggestions"
                            :key="article.url"
                            :class="[
                                'rounded-md border p-3',
                                article.is_troubleshooting
                                    ? 'border-l-4 border-l-blue-400 border-gray-200 bg-blue-50/30'
                                    : 'border-gray-200 bg-white'
                            ]"
                        >
                            <a
                                :href="article.url"
                                target="_blank"
                                rel="noopener"
                                class="text-sm font-medium text-blue-600 hover:underline"
                            >{{ article.title }}</a>
                            <p v-if="article.description" class="mt-1 text-xs text-gray-500">{{ article.description }}</p>
                        </div>
                    </div>
                </div>

                <!-- Submit -->
                <div class="mt-2">
                    <button-blue-primary @click="sendSupportTicket">
                        <div v-if="submittingTicketModal" class="flex items-center">
                            <span :class="iconLoading"></span>
                            <span>Submitting Support Ticket</span>
                        </div>
                        <div v-else>
                            <span>Submit Support Ticket</span>
                        </div>
                    </button-blue-primary>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";

import {SonicuCoreMixin} from "../mixins/SonicuCoreMixin";
import {iconNamesMixin} from "../mixins/IconNamesMixin";

import Select2 from "./elements/select2.vue";
import FloatingInput from "./elements/FloatingInput.vue";
import SonicuButton from "./elements/buttons/SonicuButton.vue";
import ButtonBluePrimary from "./elements/buttons/ButtonBluePrimary.vue";
import NotificationModal from "./elements/NotificationModal.vue";

const DEVICE_CHIP_MAP = [
    { contains: 'SoniShield Duo+',     chip: 'SoniShield Duo+' },
    { contains: 'SoniShield Duo',      chip: 'SoniShield Duo' },
    { contains: 'SoniShield Display',  chip: 'SoniShield Display' },
    { contains: 'SoniShield View',     chip: 'SoniShield View' },
    { contains: 'S-Series',            chip: 'S-Series' },
];

export default {
    name: 'Support',
    components: {NotificationModal, ButtonBluePrimary, SonicuButton, FloatingInput, Select2},
    mixins: [SonicuCoreMixin, iconNamesMixin],
    props: {
        company: {
            type: Object,
            required: true
        },
        supportText: {
            type: String,
            required: true
        },
        user: {
            type: Object,
            required: true
        },
        devices: {
            type: Object,
            required: true
        },
        troubleshootingGroups: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            supportMethods: {
                email: 'Email',
                phone: 'Phone'
            },
            replyMethod: 'email',
            phoneNumber: '',
            emailAddress: '',
            selectedDevices: [],
            description: '',
            checkedSteps: [],
            otherTroubleshootingSteps: '',

            kbSuggestions: [],
            kbLoading: false,
            debouncedFetchSuggestions: null,

            submittingTicketModal: false,
            showSubmittedTicketModal: false,
        }
    },
    created() {
        this.debouncedFetchSuggestions = debounce(this.fetchKbSuggestions, 800);
    },
    mounted() {
        this.emailAddress = ! _.isNil(this.user.email) ? this.user.email : '';
        this.phoneNumber = ! _.isNil(this.user.phone_number) ? this.user.phone_number : '';
        window.addEventListener('beforeunload', this.areYouSureYouWantToLeave);
    },
    unmounted() {
        window.removeEventListener('beforeunload', this.areYouSureYouWantToLeave);
    },
    methods: {
        inferDeviceChips() {
            const chips = [];
            for (const serial of this.selectedDevices) {
                const name = this.devices[serial] ?? '';
                for (const { contains, chip } of DEVICE_CHIP_MAP) {
                    if (name.includes(contains)) {
                        if (! chips.includes(chip)) {
                            chips.push(chip);
                        }
                        break;
                    }
                }
            }
            return chips;
        },

        fetchKbSuggestions() {
            const q = this.description.trim();
            if (q.length < 5) {
                return;
            }

            const chips = this.inferDeviceChips();
            const params = { q };
            if (chips.length > 0) {
                params.chips = chips;
            }

            this.kbLoading = true;
            axios.get(this.company.buildCompanyApiInternalRoute('support/kb-suggestions'), { params })
                .then(response => {
                    this.kbSuggestions = response.data.results ?? [];
                })
                .catch(() => {
                    this.kbSuggestions = [];
                })
                .finally(() => {
                    this.kbLoading = false;
                });
        },

        sendSupportTicket() {
            let params = {
                reply_method: this.replyMethod,
                phone_method: this.phoneNumber,
                email_method: this.emailAddress,
                devices: this.selectedDevices,
                description: this.description,
                troubleshooting_steps: this.checkedSteps,
                other_troubleshooting_steps: this.otherTroubleshootingSteps
            };

            this.submittingTicketModal = true;
            axios.post(this.company.buildCompanyApiInternalRoute('support'), params).then(response => {
                this.showSubmittedTicketModal = true;
            }).catch(error => {
                console.error('Error sending support ticket:', error);
            }).then(() => {
                this.submittingTicketModal = false;
            });
        },

        resetForm() {
            this.selectedDevices = [];
            this.description = '';
            this.checkedSteps = [];
            this.otherTroubleshootingSteps = '';
            this.kbSuggestions = [];
        },

        autoResizeTextarea(event) {
            const textarea = event.target;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
        },

        areYouSureYouWantToLeave(event) {
            event.preventDefault();
        }
    },
    watch: {
        description(val) {
            if (val.trim().length >= 5) {
                this.debouncedFetchSuggestions();
            } else {
                this.kbSuggestions = [];
            }
        },
        selectedDevices() {
            if (this.description.trim().length >= 5) {
                this.debouncedFetchSuggestions();
            }
        },
        showSubmittedTicketModal(val, old) {
            if (val !== old && val === false) {
                this.resetForm();
            }
        }
    }
}
</script>

<style scoped>
    #description {
        min-height: 10em;
        width: 100%;
        resize: none;
        overflow: hidden;
        box-sizing: border-box;
    }
</style>
